import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Layout from '../components/layout/Layout';
import { Button, LoadingState, ErrorState, EmptyState } from '../components/ui';
import { ServiceCard, ServiceModal } from '../components/services';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs, startAfter, where } from 'firebase/firestore';
import { normalizeService } from '../services/postService';
import { CATEGORIES } from '../constants/categories';

/**
 * ServicesPage - Catálogo de servicios disponibles
 */
const ServicesPage = () => {
  const { userData } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastServiceDoc, setLastServiceDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Cargar servicios iniciales
  useEffect(() => {
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const servicesRef = collection(db, 'services');
      let q;

      if (selectedCategory === 'all') {
        q = query(
          servicesRef,
          orderBy('createdAt', 'desc'),
          limit(40)
        );
      } else {
        q = query(
          servicesRef,
          where('category', '==', selectedCategory),
          orderBy('createdAt', 'desc'),
          limit(40)
        );
      }

      const snapshot = await getDocs(q);
      const loadedServices = snapshot.docs.map(doc => 
        normalizeService({
          id: doc.id,
          ...doc.data()
        })
      );

      setServices(loadedServices);
      setLastServiceDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 40);
    } catch (err) {
      console.error('Error al cargar servicios:', err);
      setError('Error al cargar los servicios');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!lastServiceDoc || loading) return;

    try {
      setLoading(true);
      const servicesRef = collection(db, 'services');
      let q;

      if (selectedCategory === 'all') {
        q = query(
          servicesRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastServiceDoc),
          limit(40)
        );
      } else {
        q = query(
          servicesRef,
          where('category', '==', selectedCategory),
          orderBy('createdAt', 'desc'),
          startAfter(lastServiceDoc),
          limit(40)
        );
      }

      const snapshot = await getDocs(q);
      const loadedServices = snapshot.docs.map(doc => 
        normalizeService({
          id: doc.id,
          ...doc.data()
        })
      );

      setServices(prev => [...prev, ...loadedServices]);
      setLastServiceDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === 40);
    } catch (err) {
      console.error('Error al cargar más servicios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceDeleted = (serviceId) => {
    setServices(prev => prev.filter(s => s.id !== serviceId));
    setSelectedService(null);
  };

  const handleServiceUpdated = (updatedService) => {
    setServices(prev => 
      prev.map(s => s.id === updatedService.id ? { ...s, ...updatedService } : s)
    );
    setSelectedService(prev => prev ? { ...prev, ...updatedService } : null);
  };

  return (
    <Layout>
      <div className="py-6">
        {/* Header */}
        <div className="container-custom mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Servicios
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Descubre y contrata servicios de ilustradores profesionales
          </p>
        </div>

        {/* Filtros de categorías */}
        <div className="container-custom mb-6">
          <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all
                  ${selectedCategory === category.id
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'
                  }
                `}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Grid de servicios */}
        <div className="container-custom">
          {/* Loading */}
          {loading && services.length === 0 && (
            <LoadingState message="Cargando servicios..." />
          )}

          {/* Error */}
          {error && (
            <ErrorState
              message={error}
              onAction={loadServices}
              actionLabel="Reintentar"
            />
          )}

          {/* Grid */}
          {!loading && services.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                {services.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onClick={setSelectedService}
                    currentUserId={userData?.id}
                    savedServiceIds={userData?.savedServices || []}
                  />
                ))}
              </div>

              {/* Botón cargar más */}
              {hasMore && (
                <div className="text-center mt-8">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    loading={loading}
                  >
                    Cargar más servicios
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!loading && services.length === 0 && (
            <EmptyState
              title="No hay servicios en esta categoría"
              description="Prueba con otra categoría o explora todas"
              icon={
                <svg className="w-10 h-10 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            />
          )}
        </div>
      </div>

      {/* Modal de detalle de servicio */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          onServiceDeleted={handleServiceDeleted}
          onServiceUpdated={handleServiceUpdated}
        />
      )}
    </Layout>
  );
};

export default ServicesPage;
